import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ID_ESTADO_SOLICITUD_PLAN } from './negocio.service';

/**
 * Servicio HTTP del tag OpenAPI **Solicitud** (`/api/v1/solicitud/...`).
 * @see http://apip-sat.tideapps.com:8002/openapi.json
 */

/** OpenAPI: ProductorSolicitudSchema (anidado en SolicitudSchema). */
export interface ProductorSolicitudSchema {
  id_productor: string;
  nombre: string;
  primer_apellido?: string | null;
  segundo_apellido?: string | null;
}

/** OpenAPI: EstadoSolicitudSchema / TipoSolicitudSchema. */
export interface EstadoTipoSolicitudSchema {
  /** En JSON suele ser número; si viniera como string, usar `obtenerIdEstadoSolicitud`. */
  id_estado_solicitud?: number | string;
  id_tipo_solicitud?: number | string;
  descripcion: string;
}

/** Estado del plan anidado en `plan` (API puede enviarlo además del `id_plan_negocio` raíz). */
export interface PlanEstadoSolicitudSchema {
  id_estado_plan_negocio?: number | string;
  descripcion?: string;
}

/** Resumen del plan en la solicitud (OpenAPI / respuesta real del listado). */
export interface PlanSolicitudSchema {
  id_plan_negocio: string;
  estado?: PlanEstadoSolicitudSchema | null;
}

/** OpenAPI: SolicitudSchema — GET .../solicitud/perfil/.../solicitudes */
export interface SolicitudSchemaApi {
  id_solicitud: number;
  /** Suele venir en raíz; si no, usar `plan.id_plan_negocio`. */
  id_plan_negocio?: string | null;
  estado: EstadoTipoSolicitudSchema;
  tipo: EstadoTipoSolicitudSchema;
  id_usuario: string;
  productor?: ProductorSolicitudSchema | null;
  /** Snapshot del plan: útil para estado del PN sin otra llamada. */
  plan?: PlanSolicitudSchema | null;
  fecha_creacion?: string | null;
  fecha_respuesta?: string | null;
  observaciones?: unknown;
}

/**
 * Item de solicitud para UI (tabla dashboard / panel).
 * Derivado de SolicitudSchema; mantiene campos planos usados en templates.
 */
export interface SolicitudItem {
  id_solicitud: number;
  /** OpenAPI: usuario asociado a la solicitud. */
  id_usuario?: string;
  nombre_productor: string;
  /** Para UI (mostrar solo nombre + primer apellido sin parsear strings). */
  productor_nombre?: string | null;
  productor_primer_apellido?: string | null;
  tipo_solicitud: string;
  estado_solicitud: string;
  /** Copia normalizada de `items[].estado.id_estado_solicitud` en la API. */
  id_estado_solicitud?: number | null;
  /** Copia normalizada de `items[].tipo.id_tipo_solicitud` (1 aprobación, 2 reapertura). */
  id_tipo_solicitud?: number | null;
  /** UUID del plan (desde API). */
  id_plan_negocio?: string;
  id_productor?: string;
  /** Desde `plan.estado` si el API lo envía. */
  id_estado_plan_negocio?: number | null;
  estado_plan_descripcion?: string | null;
  /**
   * Ítem tal cual devuelve GET `/solicitud/perfil/.../solicitudes`.
   * Necesario para armar `ultima_solicitud` al navegar a Gestión (paridad con listado de negocio).
   */
  solicitudSchemaApi?: SolicitudSchemaApi;
}

/** Respuesta paginada OpenAPI: Page[SolicitudSchema] */
export interface PageSolicitudSchemaApi {
  items: SolicitudSchemaApi[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/** Construye nombre para mostrar a partir de ProductorSolicitudSchema. */
export function construirNombreProductorSolicitud(
  productor: ProductorSolicitudSchema | null | undefined
): string {
  if (!productor) return '—';
  const partes = [productor.nombre, productor.primer_apellido, productor.segundo_apellido].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0
  );
  return partes.length ? partes.join(' ') : '—';
}

/** `items[].estado.id_estado_solicitud === 1` (pendiente / creada según negocio). */
export const ID_ESTADO_SOLICITUD_PENDIENTE = 1;

/** Lee `estado.id_estado_solicitud` aunque venga como número o string en JSON. */
export function obtenerIdEstadoSolicitud(
  estado: EstadoTipoSolicitudSchema | undefined | null
): number | null {
  if (estado == null) return null;
  const raw = estado.id_estado_solicitud;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
}

/** Lee `tipo.id_tipo_solicitud` (1 Aprobación, 2 Reapertura). */
export function obtenerIdTipoSolicitud(
  tipo: EstadoTipoSolicitudSchema | undefined | null
): number | null {
  if (tipo == null) return null;
  const raw = tipo.id_tipo_solicitud;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
}

export const ID_TIPO_SOLICITUD = {
  APROBACION: 1,
  REAPERTURA: 2,
  CIERRE: 3,
} as const;

/** ISO de `fecha_creacion` para la fecha del cuerpo del mensaje («del D de mes de AAAA»). */
function fechaReferenciaMensajeNotificacion48h(s: SolicitudSchemaApi): string | null {
  const raw = s.fecha_creacion;
  if (raw == null || typeof raw !== 'string') {
    return null;
  }
  const t = raw.trim();
  return t.length ? t : null;
}

/**
 * Mensaje del popover de notificaciones 48h según tipo de solicitud × resultado.
 * @see matriz Aprobación/Reapertura × Aprobada/Rechazada
 */
export function construirMensajeNotificacionSolicitud48h(
  s: SolicitudSchemaApi,
  formatearFecha: (iso: string | null) => string
): string {
  const idTipo = obtenerIdTipoSolicitud(s.tipo);
  const idEstado = obtenerIdEstadoSolicitud(s.estado);
  const fechaCal = formatearFecha(fechaReferenciaMensajeNotificacion48h(s));
  const nombre = construirNombreProductorSolicitud(s.productor);
  const nombrePart =
    nombre !== '—' && nombre.trim().length > 0 ? ` de ${nombre}` : '';

  if (idTipo === ID_TIPO_SOLICITUD.APROBACION && idEstado === ID_ESTADO_SOLICITUD_PLAN.APROBADA) {
    return `Se aprobó el plan de negocio del ${fechaCal}${nombrePart}`;
  }
  if (idTipo === ID_TIPO_SOLICITUD.APROBACION && idEstado === ID_ESTADO_SOLICITUD_PLAN.RECHAZADA) {
    return `Se rechazó el plan de negocio del ${fechaCal}${nombrePart}`;
  }
  if (idTipo === ID_TIPO_SOLICITUD.REAPERTURA && idEstado === ID_ESTADO_SOLICITUD_PLAN.APROBADA) {
    return `Se aprobó la reapertura del plan de negocio del ${fechaCal}${nombrePart}`;
  }
  if (idTipo === ID_TIPO_SOLICITUD.REAPERTURA && idEstado === ID_ESTADO_SOLICITUD_PLAN.RECHAZADA) {
    return `Se rechazó la reapertura del plan de negocio del ${fechaCal}${nombrePart}`;
  }

  const tipo = (s.tipo?.descripcion ?? '').trim() || '—';
  const estado = (s.estado?.descripcion ?? '').trim() || '—';
  return `${tipo} — ${estado} del ${fechaCal}${nombrePart}`;
}

/** Respuesta posible de GET `/solicitud/ultimas-48h` (arreglo o página u otro envoltorio). */
function esObjetoSolicitudApiParcial(val: unknown): val is SolicitudSchemaApi {
  if (val == null || typeof val !== 'object') return false;
  const id = (val as { id_solicitud?: unknown }).id_solicitud;
  if (id == null) return false;
  const n = typeof id === 'number' ? id : Number(String(id).trim());
  return Number.isFinite(n);
}

/**
 * Normaliza la respuesta del endpoint `ultimas-48h` a un arreglo de solicitudes.
 * Acepta `SolicitudSchemaApi[]`, `{ items }`, `{ data }` u otros envoltorios con arreglos.
 */
export function normalizarRespuestaUltimas48h(raw: unknown): SolicitudSchemaApi[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter(esObjetoSolicitudApiParcial).map(s => ({
      ...s,
      id_solicitud:
        typeof s.id_solicitud === 'number'
          ? s.id_solicitud
          : Number(String(s.id_solicitud).trim()),
    }));
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o['items'])) {
      return normalizarRespuestaUltimas48h(o['items']);
    }
    if (Array.isArray(o['data'])) {
      return normalizarRespuestaUltimas48h(o['data']);
    }
    if (Array.isArray(o['solicitudes'])) {
      return normalizarRespuestaUltimas48h(o['solicitudes']);
    }
  }
  return [];
}

/** Timestamp de `fecha_respuesta` para ordenar (0 si falta o es inválida). */
export function timestampFechaRespuestaSolicitud(s: SolicitudSchemaApi): number {
  const raw = s.fecha_respuesta;
  if (raw == null || typeof raw !== 'string' || !raw.trim()) {
    return 0;
  }
  const t = new Date(raw.trim()).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Más reciente primero (misma referencia que «Hace X min/horas/días» en el popover). */
export function ordenarSolicitudesUltimas48hPorFechaRespuesta(
  items: SolicitudSchemaApi[]
): SolicitudSchemaApi[] {
  return [...items].sort(
    (a, b) => timestampFechaRespuestaSolicitud(b) - timestampFechaRespuestaSolicitud(a)
  );
}

/**
 * Valores permitidos en `observaciones.observaciones[].seccion` (API `/solicitud/...`).
 * No usar otros literales (p. ej. `"general"`) en ese arreglo.
 */
export const SECCIONES_OBSERVACION_SOLICITUD_API = [
  'diagnostico',
  'soluciones',
  'plan_de_trabajo',
  'plan_de_inversion',
] as const;

export type SeccionObservacionSolicitudApi =
  (typeof SECCIONES_OBSERVACION_SOLICITUD_API)[number];

export function esSeccionObservacionSolicitudValida(
  valor: string
): valor is SeccionObservacionSolicitudApi {
  return (SECCIONES_OBSERVACION_SOLICITUD_API as readonly string[]).includes(valor.trim());
}

/** Cuerpo `observaciones` en crear / cambiaEstado / observacionesSolicitud. */
export interface ObservacionesSolicitudPayload {
  comentario?: string | null;
  observaciones: Array<{
    seccion: SeccionObservacionSolicitudApi;
    observacion: string;
  }>;
}

/**
 * Lee el texto libre `comentario` desde el objeto `observaciones` de una solicitud (p. ej. motivo de reapertura).
 */
export function extraerComentarioObservacionesSolicitud(obs: unknown): string | null {
  if (obs == null || typeof obs !== 'object') return null;
  const raw = (obs as ObservacionesSolicitudPayload).comentario;
  if (raw == null) return null;
  const t = String(raw).trim();
  return t.length ? t : null;
}

export interface CrearSolicitudRequest {
  id_plan_negocio: string;
  observaciones?: ObservacionesSolicitudPayload;
}

export interface CrearSolicitudResponse {
  status: string;
  id_solicitud: string;
}

export interface CambiaEstadoSolicitudRequest {
  id_solicitud: number;
  id_estado_solicitud: number;
  observaciones: ObservacionesSolicitudPayload;
}

export interface ObservacionesSolicitudRequest {
  id_solicitud: number;
  id_estado_solicitud: number;
  observaciones: ObservacionesSolicitudPayload;
}

/** `true` si el ítem de API tiene `estado.id_estado_solicitud` igual a `idEsperado`. */
export function solicitudTieneEstadoId(
  s: SolicitudSchemaApi,
  idEsperado: number
): boolean {
  return obtenerIdEstadoSolicitud(s.estado) === idEsperado;
}

function normalizarIdEstadoPlan(raw: number | string | undefined | null): number | null {
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
}

export function mapSolicitudSchemaApiToItem(s: SolicitudSchemaApi): SolicitudItem {
  const idPlan = (s.id_plan_negocio ?? s.plan?.id_plan_negocio ?? '').trim() || undefined;
  const estPlan = s.plan?.estado;
  return {
    id_solicitud: s.id_solicitud,
    id_usuario: s.id_usuario,
    nombre_productor: construirNombreProductorSolicitud(s.productor),
    productor_nombre: s.productor?.nombre ?? null,
    productor_primer_apellido: s.productor?.primer_apellido ?? null,
    tipo_solicitud: s.tipo?.descripcion ?? '—',
    estado_solicitud: s.estado?.descripcion ?? '—',
    id_estado_solicitud: obtenerIdEstadoSolicitud(s.estado),
    id_tipo_solicitud: obtenerIdTipoSolicitud(s.tipo),
    id_plan_negocio: idPlan,
    id_productor: s.productor?.id_productor,
    id_estado_plan_negocio: normalizarIdEstadoPlan(estPlan?.id_estado_plan_negocio),
    estado_plan_descripcion: estPlan?.descripcion?.trim() || null,
    solicitudSchemaApi: s,
  };
}

/**
 * Reconstruye un `SolicitudSchemaApi` mínimo desde campos planos (p. ej. datos cacheados sin `solicitudSchemaApi`).
 */
export function construirSolicitudSchemaApiDesdeItemPlano(s: SolicitudItem): SolicitudSchemaApi {
  const idPlan = (s.id_plan_negocio ?? '').trim() || null;
  return {
    id_solicitud: s.id_solicitud,
    id_plan_negocio: idPlan,
    estado: {
      descripcion: s.estado_solicitud,
      id_estado_solicitud: s.id_estado_solicitud ?? undefined,
    },
    tipo: {
      descripcion: s.tipo_solicitud,
      id_tipo_solicitud: s.id_tipo_solicitud ?? undefined,
    },
    id_usuario: s.id_usuario ?? '',
    productor:
      s.id_productor != null && String(s.id_productor).trim()
        ? {
            id_productor: String(s.id_productor).trim(),
            nombre: (s.productor_nombre ?? '').trim() || '—',
            primer_apellido: s.productor_primer_apellido ?? null,
          }
        : undefined,
    plan:
      idPlan != null
        ? {
            id_plan_negocio: idPlan,
            estado:
              s.id_estado_plan_negocio != null || (s.estado_plan_descripcion ?? '').trim()
                ? {
                    id_estado_plan_negocio: s.id_estado_plan_negocio ?? undefined,
                    descripcion: s.estado_plan_descripcion ?? undefined,
                  }
                : undefined,
          }
        : undefined,
  };
}

/**
 * Respuesta paginada normalizada para la UI (ítems ya mapeados a SolicitudItem).
 */
export interface SolicitudesPaginadasResponse {
  items: SolicitudItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Parámetros de consulta para GET .../solicitud/perfil/{id}/jerarquia/{id}/solicitudes.
 * OpenAPI: page, size, search, sort, order.
 */
export interface SolicitudesQueryParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root',
})
export class SolicitudService {
  private readonly apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  /**
   * Listado paginado de solicitudes por perfil y jerarquía (respuesta tal cual del API).
   * GET /api/v1/solicitud/perfil/{id_perfil}/jerarquia/{id_jerarquia}/solicitudes
   */
  obtenerSolicitudes(
    idPerfil: number,
    idJerarquia: number,
    queryParams: SolicitudesQueryParams = {}
  ): Observable<SolicitudesPaginadasResponse> {
    const page = queryParams.page ?? 1;
    const size = queryParams.size ?? 50;
    return this.obtenerPaginaSolicitudesRaw(idPerfil, idJerarquia, queryParams).pipe(
      map(resp => this.mapearPaginaSolicitudes(resp, page, size, () => true)),
      catchError(error => {
        console.error('Error al obtener solicitudes:', error);
        return of({
          items: [],
          total: 0,
          page: 1,
          size,
          pages: 0,
        });
      })
    );
  }

  /**
   * Misma petición que `obtenerSolicitudes`, pero deja solo ítems con
   * `items[].estado.id_estado_solicitud === ID_ESTADO_SOLICITUD_PENDIENTE` (filtro en cliente).
   */
  obtenerSolicitudesPendientes(
    idPerfil: number,
    idJerarquia: number,
    queryParams: SolicitudesQueryParams = {}
  ): Observable<SolicitudesPaginadasResponse> {
    const page = queryParams.page ?? 1;
    const size = queryParams.size ?? 50;
    return this.obtenerPaginaSolicitudesRaw(idPerfil, idJerarquia, queryParams).pipe(
      map(resp =>
        this.mapearPaginaSolicitudes(resp, page, size, s =>
          solicitudTieneEstadoId(s, ID_ESTADO_SOLICITUD_PENDIENTE)
        )
      ),
      catchError(error => {
        console.error('Error al obtener solicitudes pendientes:', error);
        return of({
          items: [],
          total: 0,
          page: 1,
          size,
          pages: 0,
        });
      })
    );
  }

  private obtenerPaginaSolicitudesRaw(
    idPerfil: number,
    idJerarquia: number,
    queryParams: SolicitudesQueryParams
  ): Observable<PageSolicitudSchemaApi> {
    let httpParams = new HttpParams();
    const page = queryParams.page ?? 1;
    const size = queryParams.size ?? 50;
    httpParams = httpParams.set('page', String(page)).set('size', String(size));
    if (queryParams.search?.trim()) {
      httpParams = httpParams.set('search', queryParams.search.trim());
    }
    if (queryParams.sort) {
      httpParams = httpParams.set('sort', queryParams.sort);
    }
    if (queryParams.order) {
      httpParams = httpParams.set('order', queryParams.order);
    }
    const url = `${this.apiUrl}/solicitud/perfil/${idPerfil}/jerarquia/${idJerarquia}/solicitudes`;
    return this.http.get<PageSolicitudSchemaApi>(url, { params: httpParams });
  }

  /**
   * Solicitudes de las últimas 48 h para un usuario (`sub` de `/me`).
   * GET /api/v1/solicitud/ultimas-48h?id_usuario=...
   */
  obtenerSolicitudesUltimas48h(idUsuario: string): Observable<SolicitudSchemaApi[]> {
    const id = idUsuario.trim();
    if (!id) {
      return of([]);
    }
    const params = new HttpParams().set('id_usuario', id);
    const url = `${this.apiUrl}/solicitud/ultimas-48h`;
    return this.http.get<unknown>(url, { params }).pipe(
      map(raw =>
        ordenarSolicitudesUltimas48hPorFechaRespuesta(normalizarRespuestaUltimas48h(raw))
      ),
      catchError(error => {
        console.error('Error al obtener solicitudes últimas 48h:', error);
        return of([]);
      })
    );
  }

  private mapearPaginaSolicitudes(
    resp: PageSolicitudSchemaApi,
    page: number,
    size: number,
    incluir: (s: SolicitudSchemaApi) => boolean
  ): SolicitudesPaginadasResponse {
    const filas = (resp.items ?? []).filter(incluir);
    return {
      items: filas.map(mapSolicitudSchemaApiToItem),
      total: resp.total ?? 0,
      page: resp.page ?? page,
      size: resp.size ?? size,
      pages: resp.pages ?? 0,
    };
  }

  /**
   * POST /api/v1/solicitud/crearSolicitud?id_tipo_solicitud=1|2
   */
  crearSolicitud(
    idTipoSolicitud: typeof ID_TIPO_SOLICITUD.APROBACION | typeof ID_TIPO_SOLICITUD.REAPERTURA | typeof ID_TIPO_SOLICITUD.CIERRE,
    body: CrearSolicitudRequest
  ): Observable<CrearSolicitudResponse> {
    const params = new HttpParams().set('id_tipo_solicitud', String(idTipoSolicitud));
    const url = `${this.apiUrl}/solicitud/crearSolicitud`;
    return this.http.post<CrearSolicitudResponse>(url, body, { params });
  }

  /**
   * PUT /api/v1/solicitud/cambiaEstadoSolicitud — aprobar (2), rechazar (3), etc.
   */
  cambiaEstadoSolicitud(payload: CambiaEstadoSolicitudRequest): Observable<SolicitudSchemaApi> {
    const url = `${this.apiUrl}/solicitud/cambiaEstadoSolicitud`;
    return this.http.put<SolicitudSchemaApi>(url, payload);
  }

  /**
   * PUT /api/v1/solicitud/observacionesSolicitud — guardar comentario por sección (TAB).
   */
  observacionesSolicitud(payload: ObservacionesSolicitudRequest): Observable<SolicitudSchemaApi> {
    const url = `${this.apiUrl}/solicitud/observacionesSolicitud`;
    return this.http.put<SolicitudSchemaApi>(url, payload);
  }
}
